"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TipLocationSchema = exports.TIP_LOCATIONS = exports.SUNMI_T3_SINGLE_POLICY = exports.SUNMI_T3_DUAL_POLICY = exports.CardSetupPolicySchema = exports.CardSetupPolicyReaderSchema = exports.CardSetupProfileSchema = exports.CARD_SETUP_PROFILES = exports.SUNBAY_READER_MODELS = exports.SUNBAY_CONNECTION_MODES = exports.SUNBAY_DEVICE_CLASS = exports.MAGTEK_MODEL_FOR_KIND = exports.MAGTEK_READER_MODELS = exports.MAGTEK_DEVICE_CLASS = exports.READER_ROLE_LABELS = exports.READER_NEUTRAL_NAMES = exports.DEFAULT_READER_CAPABILITIES = exports.ReaderCapabilitiesOverrideSchema = exports.ReaderCapabilitiesSchema = exports.ReaderKindSchema = exports.READER_KINDS = void 0;
exports.readerNeutralName = readerNeutralName;
exports.readerKindOf = readerKindOf;
exports.capabilitiesOf = capabilitiesOf;
exports.isCardSetupModel = isCardSetupModel;
exports.cardSetupPolicyOf = cardSetupPolicyOf;
exports.policyAllows = policyAllows;
exports.debitReaderIdOf = debitReaderIdOf;
exports.debitPossible = debitPossible;
exports.surchargePossible = surchargePossible;
exports.tipLocations = tipLocations;
exports.tipLocationAllowed = tipLocationAllowed;
exports.defaultTipLocation = defaultTipLocation;
exports.cardSetupLocks = cardSetupLocks;
const zod_1 = require("zod");
/**
 * CARD READERS — what a reader can do, and which readers a till family may use.
 * Spec: docs/agenticpos-rubytill/sunmi-card-setup.md §1–§3 (owner-approved 2026-10-14).
 *
 * VENDOR-NEUTRAL BY CONSTRUCTION. Logic never branches on a vendor name:
 *   - a reader is described by its CAPABILITIES (catalog `readerCapabilities`,
 *     falling back to `DEFAULT_READER_CAPABILITIES[kind]`);
 *   - a till family is described by its catalog POLICY (`cardSetupPolicy`).
 * A `ReaderKind` names a LANE FAMILY (how the till reaches the reader), and
 * vendor code lives only in those lanes. A new vendor = catalog rows + a lane +
 * one additive enum value here — never a rename.
 *
 * Everything in this file is PURE (no I/O), so the server, Super Admin, the
 * merchant app and the till compute the same locks from the same data.
 */
// ── Reader kinds + capabilities ──────────────────────────────────────────────
/** Additive enum — a kind is a lane family. Never rename a value. */
exports.READER_KINDS = ['tap_to_pay', 'dejavoo_p', 'magtek_ped', 'magtek_go', 'sunbay_tapro'];
exports.ReaderKindSchema = zod_1.z.enum(exports.READER_KINDS);
exports.ReaderCapabilitiesSchema = zod_1.z.object({
    credit: zod_1.z.boolean(),
    pinDebit: zod_1.z.boolean(),
    tap: zod_1.z.boolean(),
    dip: zod_1.z.boolean(),
    swipe: zod_1.z.boolean(),
    /** A customer-facing screen on the reader/terminal itself. */
    screen: zod_1.z.boolean(),
    tipOnScreen: zod_1.z.boolean(),
    signatureOnScreen: zod_1.z.boolean(),
});
/**
 * A catalog row's override. Partial on purpose: an editor that knows one key
 * more than the service must not fail a save, and a missing key keeps the
 * kind's default.
 */
exports.ReaderCapabilitiesOverrideSchema = exports.ReaderCapabilitiesSchema.partial();
const caps = (c) => ({
    credit: false, pinDebit: false, tap: false, dip: false, swipe: false,
    screen: false, tipOnScreen: false, signatureOnScreen: false, ...c,
});
exports.DEFAULT_READER_CAPABILITIES = {
    // Phone/till NFC: credit by tap only — no PIN debit, no chip/stripe, no signature.
    tap_to_pay: caps({ credit: true, tap: true, screen: true, tipOnScreen: true }),
    dejavoo_p: caps({ credit: true, pinDebit: true, tap: true, dip: true, swipe: true, screen: true, tipOnScreen: true, signatureOnScreen: true }),
    // tipOnScreen unverified on the PED → false until proven on a bench.
    magtek_ped: caps({ credit: true, pinDebit: true, tap: true, dip: true, swipe: true, screen: true }),
    magtek_go: caps({ credit: true, tap: true, dip: true, swipe: true }),
    // SUNMI CPad Pay running SUNBAY Tapro: hardware truth; the lane (laneLimits) narrows PIN debit, tip and signature.
    sunbay_tapro: caps({ credit: true, pinDebit: true, tap: true, dip: true, swipe: true, screen: true, tipOnScreen: true, signatureOnScreen: true }),
};
/**
 * Neutral names — what a merchant, a back-office user or a cashier reads.
 * NEVER a vendor, processor or protocol name (owner rule 4).
 */
exports.READER_NEUTRAL_NAMES = {
    tap_to_pay: 'Tap to Pay on this till',
    dejavoo_p: 'Countertop card reader',
    magtek_ped: 'Countertop card reader',
    magtek_go: 'Compact card reader',
    sunbay_tapro: 'Customer payment screen',
};
exports.READER_ROLE_LABELS = {
    primary: 'Primary reader',
    backup: 'Backup reader',
};
/** The neutral name for a kind — "Card reader" when the kind is unknown. */
function readerNeutralName(kind) {
    return (kind && exports.READER_NEUTRAL_NAMES[kind]) || 'Card reader';
}
const isReaderKind = (v) => typeof v === 'string' && exports.READER_KINDS.includes(v);
/**
 * MAGTEK (USB) DEVICE ROWS — one device class, the reader model decides the kind.
 * `deviceClass: 'magtek_usb'`, `model` one of MAGTEK_READER_MODELS. A row with an
 * unknown model resolves to no kind (fails closed — never guessed as the PED).
 */
exports.MAGTEK_DEVICE_CLASS = 'magtek_usb';
exports.MAGTEK_READER_MODELS = {
    'MagTek DynaFlex II PED': 'magtek_ped',
    'MagTek DynaFlex II Go': 'magtek_go',
};
/** The model name for a MagTek kind (registration forms). */
exports.MAGTEK_MODEL_FOR_KIND = {
    magtek_ped: 'MagTek DynaFlex II PED',
    magtek_go: 'MagTek DynaFlex II Go',
};
/**
 * SUNBAY TAPRO DEVICE ROWS — the SUNMI CPad Pay running Tapro. `deviceClass:
 * 'sunbay_tapro'`, `model` one of SUNBAY_READER_MODELS; unknown model → no kind
 * (fails closed). `sunbayConnection` says where Tapro runs relative to the till.
 */
exports.SUNBAY_DEVICE_CLASS = 'sunbay_tapro';
/* Taplink cross-device CABLE (2026-10-29, SUNBAY): cable_vsp = a CPad Pay on a USB cable (USB_VSP);
   cable_aoa = a handheld like the SUNMI P3H on a USB cable (USB_AOA). sub_screen is ONLY a CPad docked
   in the T3's own connect dock. */
exports.SUNBAY_CONNECTION_MODES = ['sub_screen', 'on_device', 'cable_aoa', 'cable_vsp'];
exports.SUNBAY_READER_MODELS = {
    'SUNMI CPad Pay': 'sunbay_tapro',
};
/**
 * The lane family of an EMV device, or null when it is not a card reader this
 * setup knows.
 *   rtpay (`dejavoo_rubytill_pay`) + cardEngine 'tap_to_pay' → tap_to_pay
 *   rtpay + cardEngine 'sunbay' (CPad Pay running RubyTill Pay) → sunbay_tapro
 *   `dejavoo_spin_usb` | `dejavoo_spin_cloud` | rtpay on DvPayLite → dejavoo_p
 *   `magtek_usb` + model (MAGTEK_READER_MODELS) → magtek_ped | magtek_go
 *   `sunbay_tapro` + model (SUNBAY_READER_MODELS) → sunbay_tapro
 * An explicit, valid `readerKind` on the row wins (additive lanes).
 */
function readerKindOf(device) {
    if (!device)
        return null;
    if (isReaderKind(device.readerKind))
        return device.readerKind;
    const cls = String(device.deviceClass || '');
    if (cls === 'dejavoo_rubytill_pay') {
        const engine = device.rtpay?.cardEngine ?? device.rtpayCardEngine;
        // A CPad Pay running RubyTill Pay reads cards with Tapro — it is a CPad, never a P17.
        return engine === 'tap_to_pay' ? 'tap_to_pay' : engine === 'sunbay' ? 'sunbay_tapro' : 'dejavoo_p';
    }
    if (cls === 'dejavoo_spin_usb' || cls === 'dejavoo_spin_cloud')
        return 'dejavoo_p';
    if (cls === exports.MAGTEK_DEVICE_CLASS)
        return exports.MAGTEK_READER_MODELS[String(device.model || '')] ?? null;
    if (cls === exports.SUNBAY_DEVICE_CLASS)
        return exports.SUNBAY_READER_MODELS[String(device.model || '')] ?? null;
    return null;
}
/**
 * What this device can do: the kind's default, with the catalog row's
 * `readerCapabilities` laid over it key by key. Null when the kind is unknown.
 */
function capabilitiesOf(device, catalogRow) {
    const kind = readerKindOf(device);
    if (!kind)
        return null;
    const base = exports.DEFAULT_READER_CAPABILITIES[kind];
    const o = catalogRow?.readerCapabilities;
    if (!o || typeof o !== 'object')
        return { ...base };
    const out = { ...base };
    for (const k of Object.keys(base)) {
        if (typeof o[k] === 'boolean')
            out[k] = o[k];
    }
    return out;
}
// ── Device profile policy (catalog `pos_terminal` rows) ─────────────────────
/** Install-profile family. Additive enum. */
exports.CARD_SETUP_PROFILES = ['sunmi_t3'];
exports.CardSetupProfileSchema = zod_1.z.enum(exports.CARD_SETUP_PROFILES);
exports.CardSetupPolicyReaderSchema = zod_1.z.object({
    kind: exports.ReaderKindSchema,
    /** May be the primary reader. */
    primary: zod_1.z.boolean(),
    /** May be the backup reader. */
    backup: zod_1.z.boolean(),
    /** false = shown as "coming next", never selectable. */
    enabled: zod_1.z.boolean(),
});
exports.CardSetupPolicySchema = zod_1.z.object({
    profile: exports.CardSetupProfileSchema,
    variant: zod_1.z.enum(['single', 'dual']),
    /** The till has a customer-facing display (dual = true). */
    customerDisplay: zod_1.z.boolean(),
    readers: zod_1.z.array(exports.CardSetupPolicyReaderSchema).min(1).max(16),
    defaultPrimary: exports.ReaderKindSchema,
    defaultBackup: zod_1.z.union([exports.ReaderKindSchema, zod_1.z.literal('none')]),
    allowNoBackup: zod_1.z.boolean(),
});
/**
 * Does this catalog model run the card setup? = it carries a VALID
 * `cardSetupPolicy`. Never decided by a name or a vendor. Accepts any partial
 * row (a list item, a raw DynamoDB item). Null/undefined ⇒ false.
 */
function isCardSetupModel(row) {
    return !!row && exports.CardSetupPolicySchema.safeParse(row.cardSetupPolicy).success;
}
/** The parsed policy, or null. */
function cardSetupPolicyOf(row) {
    if (!row)
        return null;
    const p = exports.CardSetupPolicySchema.safeParse(row.cardSetupPolicy);
    return p.success ? p.data : null;
}
/** Is `kind` allowed in `role` by this policy (and enabled)? */
function policyAllows(policy, kind, role) {
    if (!kind)
        return false;
    const r = policy.readers.find((x) => x.kind === kind);
    return !!r && r.enabled && (role === 'primary' ? r.primary : r.backup);
}
/** The two dev catalog policies (spec §2) — seed data and test fixtures. */
exports.SUNMI_T3_DUAL_POLICY = {
    profile: 'sunmi_t3', variant: 'dual', customerDisplay: true,
    readers: [
        { kind: 'tap_to_pay', primary: true, backup: true, enabled: true },
        { kind: 'dejavoo_p', primary: true, backup: true, enabled: true },
        { kind: 'magtek_ped', primary: true, backup: true, enabled: true },
        { kind: 'magtek_go', primary: true, backup: true, enabled: true },
    ],
    defaultPrimary: 'tap_to_pay', defaultBackup: 'dejavoo_p', allowNoBackup: true,
};
exports.SUNMI_T3_SINGLE_POLICY = {
    profile: 'sunmi_t3', variant: 'single', customerDisplay: false,
    readers: [
        { kind: 'dejavoo_p', primary: true, backup: true, enabled: true },
        { kind: 'tap_to_pay', primary: true, backup: true, enabled: true },
        { kind: 'magtek_ped', primary: true, backup: true, enabled: true },
        { kind: 'magtek_go', primary: true, backup: true, enabled: true },
    ],
    defaultPrimary: 'dejavoo_p', defaultBackup: 'tap_to_pay', allowNoBackup: true,
};
// ── Payment-option locks (computed; stored fields unchanged) ────────────────
exports.TIP_LOCATIONS = ['customer_display', 'terminal', 'off'];
exports.TipLocationSchema = zod_1.z.enum(exports.TIP_LOCATIONS);
/** First reader (primary first) that takes PIN debit — the debit route. */
function debitReaderIdOf(readers) {
    const ordered = [...readers].sort((a, b) => (a.role === 'backup' ? 1 : 0) - (b.role === 'backup' ? 1 : 0));
    return ordered.find((r) => r.capabilities?.pinDebit === true)?.emvDeviceId ?? null;
}
/** Debit is possible ⇔ some reader takes PIN debit. */
function debitPossible(s) {
    return !!s && typeof s.debitReaderId === 'string' && !!s.debitReaderId;
}
/** surcharge ⇒ debit (existing compliance rule) ⇒ surcharge possible ⇔ debit possible. */
function surchargePossible(s) {
    return debitPossible(s);
}
/**
 * Every tip location, each with whether it is allowed here and why not.
 *   customer_display — needs a customer display (policy/resolved `customerDisplay`)
 *   terminal         — needs some reader with `tipOnScreen`
 *   off              — always allowed
 */
function tipLocations(s) {
    const anyTipScreen = s.readers.some((r) => r.capabilities?.tipOnScreen === true);
    return [
        { value: 'customer_display', allowed: !!s.customerDisplay, reason: s.customerDisplay ? null : 'This till has no customer display.' },
        { value: 'terminal', allowed: anyTipScreen, reason: anyTipScreen ? null : 'No card reader on this till can ask for a tip.' },
        { value: 'off', allowed: true, reason: null },
    ];
}
function tipLocationAllowed(s, loc) {
    return tipLocations(s).some((o) => o.value === loc && o.allowed);
}
/** Smart default: customer display if there is one, else the terminal, else off. */
function defaultTipLocation(s) {
    return tipLocations(s).find((o) => o.allowed).value;
}
/** All locks at once, with reasons — what every surface shows. */
function cardSetupLocks(s) {
    const debitReaderId = s.debitReaderId !== undefined ? s.debitReaderId : debitReaderIdOf(s.readers);
    const d = debitPossible({ debitReaderId });
    return {
        debit: { possible: d, reason: d ? null : 'Debit needs a card reader with a PIN pad.' },
        surcharge: { possible: d, reason: d ? null : 'Surcharge needs debit, and debit needs a card reader with a PIN pad.' },
        tipLocations: tipLocations(s),
    };
}
//# sourceMappingURL=cardReaders.js.map